// ============================================================================
// streamManager.js  --  page-scoped lifecycle for live phone streams.
//
// Separation of concerns (this is the heart of the owner's bandwidth model):
//   * SOCKET lifecycle is PAGE-scoped. syncPage() opens a socket for every phone
//     on the current page and closes sockets for phones that left the page. That
//     is the ONLY place streams start/stop on navigation -- a deliberate act.
//     There is deliberately NO IntersectionObserver: every card on the page
//     streams continuously at LOW while the page is shown. Bandwidth is bounded
//     by phones-per-page, a number the USER picked.
//   * DISPLAY attachment is CARD/MODAL-scoped. The canvas is a DOM node moved
//     between the grid card and the expand modal; moving it never touches the
//     socket, so expand/collapse are pure in-place quality changes.
//   * QUALITY is per-stream. Expand raises ONE stream to HIGH on its existing
//     socket (sendNewVideoSetting); siblings are never touched, so they stay LOW.
//
//   * TAB FOCUS is the one acceptable stop/start: attachVisibilityAutostop()
//     freezes every socket when the tab is hidden (nobody is watching) and
//     resumes the current page on return, debounced so a quick alt-tab is a no-op.
//
//   * HONEST STATE (2026-09-29, monitor video self-heal V3): every stream reports its state
//     (connecting / waiting / starting / streaming / no-picture / retrying / paused) into a small
//     store keyed by stream key. The page reads it with useSyncExternalStore. It is deliberately NOT
//     part of `streamsTick`, so a state change never re-homes a canvas.
//
//   * AUTO RE-PROBE (MF7, 2026-09-29, the page's `reprobe` switch, AUTO route only). A tile on the
//     server route - the page's first probe found no relay here (the owner's D6 starts the relay with
//     the first viewer), or its direct socket failed - tries the direct route again in the background:
//     soon after the PC says its relay started, and on a backoff (REPROBE.DELAYS_MS). The trial is a
//     second, one-shot socket to ws://localhost:6847 with its own canvas. Only when it has DECODED ITS
//     FIRST FRAME does it become the tile's stream (the canvas is swapped in place); then the server
//     socket closes, so the server drops that subscription. A trial that fails changes nothing on the
//     tile. Hysteresis: a tile that falls back to the server after such a switch waits REPROBE.HYST_MS
//     (doubling per flip in 30 min, capped) before the next trial; a phone the relay here does not have
//     is tried again only after 10 min, then 30 min.
//
// Framework-agnostic on purpose: PhoneMonitorPage.js drives it from React
// effects, and the Playwright proof harness drives the SAME code, so the proof
// exercises exactly what ships.
// ============================================================================

import { ScrcpyStream } from './scrcpyStream';

/** MF7 AUTO re-probe timings. Exported so tests and the page read the same numbers. */
export const REPROBE = Object.freeze({
    DELAYS_MS: [3000, 15000, 30000, 60000, 120000, 300000, 600000], // after a server-route picture; the last repeats
    RELAY_HINT_MS: 1500, // the PC said its relay started: try direct this soon
    TRIAL_MS: 10000, // a trial with no decoded frame in 10 s failed
    NOT_HERE_MS: [600000, 1800000], // the relay here answered but has no such phone: 10 min, then every 30 min
    HYST_MS: 120000, // after a direct -> server flip: no trial for 2 min ...
    HYST_MAX_MS: 1800000, // ... doubling per flip within FLIP_WINDOW_MS, at most 30 min
    FLIP_WINDOW_MS: 1800000,
});

function canvasFor() {
    const canvas = document.createElement('canvas');
    canvas.className = 'necro-stream-canvas';
    canvas.width = 216;
    canvas.height = 480;
    // Portable display styling so the same canvas fits both a grid card and
    // the expand modal (parents are sized; object-fit keeps device aspect).
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.objectFit = 'contain';
    canvas.style.display = 'block';
    canvas.style.background = '#000';
    return canvas;
}

const fmtS = (ms) => `${Math.round(Math.max(0, ms) / 100) / 10} s`;

class StreamManager {
    constructor() {
        this.map = new Map(); // key -> { stream, canvas, serial, installId, pc }
        this.statuses = new Map(); // key -> frozen status snapshot
        this.keyListeners = new Map(); // key -> Set<fn>
        this.allListeners = new Set();
        this.version = 0;
        this.hooks = {}; // { onAuthSuspect(key), log(line), rng() }
        // Bound so React can pass them straight to useSyncExternalStore.
        this.subscribe = this.subscribe.bind(this);
        this.subscribeAll = this.subscribeAll.bind(this);
        this.getStatus = this.getStatus.bind(this);
        this.getVersion = this.getVersion.bind(this);
    }

    /** Page-level hooks: { onAuthSuspect(key), log(line), rng() }. Merged, never replaced wholesale. */
    configure(hooks = {}) {
        this.hooks = { ...this.hooks, ...hooks };
    }

    /** The tile-stream callbacks for `key`; each ignores a stream that is no longer the tile's (a swapped-out one). */
    _callbacks(key, holder) {
        const mine = () => {
            const e = this.map.get(key);
            return !!e && e.stream === holder.stream;
        };
        return {
            onState: (st) => {
                if (mine()) this._setStatus(key, st);
            },
            onAuthSuspect: () => {
                if (mine() && this.hooks.onAuthSuspect) this.hooks.onAuthSuspect(key);
            },
            onHint: (kind, detail) => {
                if (mine()) this._onHint(key, kind, detail);
            },
        };
    }

    _log(line) {
        const log = this.hooks.log;
        try {
            if (typeof log === 'function') log(line);
            // eslint-disable-next-line no-console
            else console.info(line);
        } catch (e) {
            /* noop */
        }
    }

    _make(key, serial, base, opts = {}) {
        const canvas = canvasFor();
        // Transport (local ws-scrcpy vs remote Spring) is threaded through here; the decoder is the
        // same either way. installId is required only for the remote transport.
        const rng = this.hooks.rng || Math.random;
        const holder = {};
        const stream = new ScrcpyStream({
            serial, canvas, quality: 'low', base,
            transport: opts.transport, installId: opts.installId, apiBase: opts.apiBase,
            // Self-heal (all off unless the page's resolved config turns them on).
            heal: !!opts.reconnect,
            autoFallback: !!opts.autoFallback,
            hold: !!opts.reconnect && !!opts.hold,
            remoteLow: opts.remoteLow,
            remoteHigh: opts.remoteHigh,
            label: opts.pc || '',
            rng,
            log: this.hooks.log,
            ...this._callbacks(key, holder),
        });
        holder.stream = stream;
        this.map.set(key, {
            stream, canvas, serial, installId: opts.installId, pc: opts.pc || '', base, opts,
            up: { timer: null, attempt: 0, notHere: 0, flips: [], swappedAt: 0, trial: null, lastRoute: null, notBefore: 0 },
        });
        if (opts.stagger) {
            // Review M5: a page's first opens (and every resume) are spread over 0-1 s, so a server
            // restart or a Refresh never dials every tile of every open Monitor on the same second.
            stream.connectAfter(Math.round(rng() * (opts.staggerMs ?? 1000)));
        } else {
            stream.connect();
        }
        return this.map.get(key);
    }

    /**
     * Reconcile live sockets to exactly the phones on the current page.
     * @param {{key:string, serial:string, installId?:number|string, pc?:string}[]} entries phones on the current page
     * @param {string} [base] ws base origin (local transport)
     * @param {object} [opts] {transport:'local'|'remote', apiBase, reconnect, autoFallback, remoteLow, stagger}
     */
    syncPage(entries, base, opts = {}) {
        const want = new Set(entries.map((e) => e.key));
        for (const key of [...this.map.keys()]) {
            if (!want.has(key)) this.release(key);
        }
        for (const e of entries) {
            if (!this.map.has(e.key)) {
                this._make(e.key, e.serial, base, {
                    ...opts, installId: e.installId, pc: e.pc,
                });
            }
        }
    }

    getCanvas(key) {
        const e = this.map.get(key);
        return e ? e.canvas : null;
    }

    getStream(key) {
        const e = this.map.get(key);
        return e ? e.stream : null;
    }

    /** Raise/lower ONE stream in place on its existing socket (no reconnect). */
    setQuality(key, quality) {
        const e = this.map.get(key);
        if (e) e.stream.setQuality(quality);
    }

    /** The per-tile Retry button: that tile's counters reset and it reconnects now. */
    retry(key) {
        const e = this.map.get(key);
        if (e) e.stream.retry();
    }

    /** Stop every tile with one reason (signed out); each keeps its canvas and offers Retry. */
    giveUpAll(kind, text) {
        for (const e of this.map.values()) e.stream.giveUp(kind, text);
    }

    release(key) {
        const e = this.map.get(key);
        if (!e) return;
        this._cancelUpgrade(e);
        e.stream.stop();
        if (e.canvas && e.canvas.parentNode) e.canvas.parentNode.removeChild(e.canvas);
        this.map.delete(key);
        this._deleteStatus(key);
    }

    stopAll() {
        for (const key of [...this.map.keys()]) this.release(key);
    }

    has(key) {
        return this.map.has(key);
    }

    stats() {
        return [...this.map.entries()].map(([key, e]) => ({
            key, pc: e.pc, ...e.stream.stats(),
            reprobe: e.up ? {
                trial: !!e.up.trial, scheduled: !!e.up.timer, attempt: e.up.attempt, notHere: e.up.notHere,
                flips: e.up.flips.length, swappedAt: e.up.swappedAt || null,
            } : null,
        }));
    }

    // ------------------------------------------------------------ status store (V3)

    getStatus(key) {
        return this.statuses.get(key) || null;
    }

    getVersion() {
        return this.version;
    }

    /** Snapshot of every live status: [{key, ...status}]. */
    allStatuses() {
        return [...this.statuses.entries()].map(([key, st]) => ({ key, ...st }));
    }

    subscribe(key, fn) {
        let set = this.keyListeners.get(key);
        if (!set) {
            set = new Set();
            this.keyListeners.set(key, set);
        }
        set.add(fn);
        return () => {
            const s = this.keyListeners.get(key);
            if (!s) return;
            s.delete(fn);
            if (s.size === 0) this.keyListeners.delete(key);
        };
    }

    subscribeAll(fn) {
        this.allListeners.add(fn);
        return () => this.allListeners.delete(fn);
    }

    _setStatus(key, st) {
        // Only streams this manager still owns report (release() deletes the entry right after stop()).
        const e = this.map.get(key);
        if (!e) return;
        this.statuses.set(key, st);
        this._notify(key);
        if (e.up) this._watchRoute(key, e, st);
    }

    // ------------------------------------------------------------ AUTO re-probe (MF7)

    _upgradeAllowed(e) {
        const o = e.opts || {};
        return !!(o.reprobe && o.reconnect && e.installId != null && typeof document !== 'undefined');
    }

    /** Route bookkeeping on every status: schedule a trial for a server-route picture; record a direct -> server flip. */
    _watchRoute(key, e, st) {
        const up = e.up;
        const prev = up.lastRoute;
        up.lastRoute = st.route;
        if (!this._upgradeAllowed(e)) return;
        const t = Date.now();
        if (prev === 'local' && st.route === 'remote' && up.swappedAt) {
            up.flips = up.flips.filter((x) => t - x < REPROBE.FLIP_WINDOW_MS);
            up.flips.push(t);
            const hold = Math.min(REPROBE.HYST_MAX_MS, REPROBE.HYST_MS * 2 ** (up.flips.length - 1));
            up.notBefore = t + hold;
            up.attempt = 0;
            this._log(`[monitor] ${e.pc || 'PC'}/${e.serial}: re-probe - the tile fell back to the server route ${fmtS(t - up.swappedAt)} after`
                + ` switching to direct (flip ${up.flips.length} in 30 min); no direct trial for ${fmtS(hold)}`);
        }
        if (st.route === 'remote' && st.state === 'streaming' && !up.timer && !up.trial) {
            this._scheduleTrial(key, e, null);
        }
    }

    _onHint(key, kind) {
        const e = this.map.get(key);
        if (!e || !this._upgradeAllowed(e) || kind !== 'relay-started') return;
        if (e.stream.status.route !== 'remote' || e.up.trial) return;
        // The PC's relay just started (the owner's D6 starts it with the first viewer): try direct now, fresh backoff.
        e.up.attempt = 0;
        this._scheduleTrial(key, e, REPROBE.RELAY_HINT_MS, 'the PC says its relay started');
    }

    _cancelUpgrade(e) {
        const up = e.up;
        if (!up) return;
        if (up.timer) clearTimeout(up.timer);
        up.timer = null;
        if (up.trial) this._endTrial(e, 'the tile closed');
    }

    _scheduleTrial(key, e, fixedMs, why) {
        const up = e.up;
        if (up.timer) clearTimeout(up.timer);
        const t = Date.now();
        let delay;
        if (fixedMs != null) {
            delay = fixedMs;
        } else if (up.notHere) {
            delay = REPROBE.NOT_HERE_MS[Math.min(up.notHere - 1, REPROBE.NOT_HERE_MS.length - 1)];
        } else {
            delay = REPROBE.DELAYS_MS[Math.min(up.attempt, REPROBE.DELAYS_MS.length - 1)];
        }
        delay = Math.max(delay, up.notBefore - t);
        up.timer = setTimeout(() => {
            up.timer = null;
            this._startTrial(key);
        }, delay);
        this._log(`[monitor] ${e.pc || 'PC'}/${e.serial}: re-probe - next direct trial in ${fmtS(delay)}`
            + `${why ? ` (${why})` : ''}${up.notHere ? ' (the relay here did not have this phone last time)' : ''}`);
    }

    _startTrial(key) {
        const e = this.map.get(key);
        if (!e || e.up.trial || !this._upgradeAllowed(e)) return;
        if (e.stream.status.route !== 'remote') return; // already direct
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
            this._scheduleTrial(key, e, null, 'the tab is hidden');
            return;
        }
        const up = e.up;
        const canvas = canvasFor();
        const t0 = Date.now();
        const trial = {
            t0, canvas, stream: null, timer: null, opened: false, initial: false,
        };
        up.trial = trial;
        this._log(`[monitor] ${e.pc || 'PC'}/${e.serial}: re-probe - trying the direct route (trial ${up.attempt + 1})`);
        const stream = new ScrcpyStream({
            serial: e.serial, canvas, quality: e.stream.quality, base: e.base,
            transport: 'local', installId: e.installId, apiBase: e.opts.apiBase,
            heal: false, autoFallback: false, hold: false, // one-shot: a trial never retries by itself
            remoteLow: e.opts.remoteLow, remoteHigh: e.opts.remoteHigh,
            label: e.pc || '', rng: this.hooks.rng || Math.random, log: this.hooks.log, logTag: 're-probe trial: ',
            onState: (st) => this._onTrialState(key, trial, st),
        });
        trial.stream = stream;
        trial.timer = setTimeout(() => this._trialFailed(key, trial, `no picture on the direct route in ${fmtS(REPROBE.TRIAL_MS)}`), REPROBE.TRIAL_MS);
        stream.connect();
    }

    _onTrialState(key, trial, st) {
        const e = this.map.get(key);
        if (!e || e.up.trial !== trial) return;
        if (st.state === 'waiting' || st.state === 'starting') trial.opened = true;
        if (st.state === 'starting') trial.initial = true;
        if (st.state === 'streaming') {
            this._swapToTrial(key, e, trial);
        } else if (st.state === 'paused') {
            this._trialFailed(key, trial, st.reason || 'the direct socket closed');
        }
    }

    _endTrial(e, why) {
        const trial = e.up.trial;
        if (!trial) return;
        e.up.trial = null;
        if (trial.timer) clearTimeout(trial.timer);
        if (trial.stream) {
            trial.stream.onState = null;
            trial.stream.stop();
        }
        return why;
    }

    _trialFailed(key, trial, why) {
        const e = this.map.get(key);
        if (!e || e.up.trial !== trial) return;
        const up = e.up;
        const s = trial.stream;
        const opened = trial.opened || (s && s.opens > 0);
        const initial = trial.initial || (s && s._everInitial);
        this._endTrial(e, why);
        // Opened but never started a stream: the relay here answers, but this phone is not on this PC.
        if (opened && !initial) up.notHere++;
        else up.attempt++;
        this._log(`[monitor] ${e.pc || 'PC'}/${e.serial}: re-probe - the direct trial failed after ${fmtS(Date.now() - trial.t0)}: ${why}`
            + `${opened && !initial ? ' (the relay here answered but did not start this phone - it is on another PC?)' : !opened ? ' (no relay answering here)' : ''}`
            + '; the tile stays on the server route');
        if (e.stream.status.route === 'remote') this._scheduleTrial(key, e, null);
    }

    _swapToTrial(key, e, trial) {
        const up = e.up;
        const old = e.stream;
        const oldCanvas = e.canvas;
        const next = trial.stream;
        up.trial = null;
        if (trial.timer) clearTimeout(trial.timer);
        const holder = { stream: next };
        const cb = this._callbacks(key, holder);
        e.stream = next;
        e.canvas = trial.canvas;
        next.promote({
            heal: !!e.opts.reconnect, hold: !!e.opts.reconnect && !!e.opts.hold,
            autoFallback: true, // the relay here may stop later: back to the server route (with the hysteresis above)
            remoteLow: e.opts.remoteLow, remoteHigh: e.opts.remoteHigh,
            log: this.hooks.log, ...cb,
        });
        if (next.quality !== old.quality) next.setQuality(old.quality);
        if (oldCanvas && oldCanvas.parentNode) oldCanvas.parentNode.replaceChild(trial.canvas, oldCanvas);
        old.onState = null;
        old.onHint = null;
        old.stop(); // the server socket closes: the server drops this subscription
        up.swappedAt = Date.now();
        up.attempt = 0;
        up.notHere = 0;
        up.lastRoute = 'local';
        this._log(`[monitor] ${e.pc || 'PC'}/${e.serial}: re-probe - switched to the direct route (its first frame came`
            + ` ${fmtS(up.swappedAt - trial.t0)} after the trial opened); the server-route socket is closed`);
        this._setStatus(key, next.status);
        if (this.hooks.onSwap) {
            try {
                this.hooks.onSwap(key, 'local');
            } catch (err) {
                /* the page's problem */
            }
        }
    }

    _deleteStatus(key) {
        if (!this.statuses.has(key)) return;
        this.statuses.delete(key);
        this._notify(key);
    }

    _notify(key) {
        this.version++;
        const set = this.keyListeners.get(key);
        if (set) for (const fn of [...set]) fn();
        for (const fn of [...this.allListeners]) fn();
    }
}

// One monitor page in the app -> one shared manager.
export const streamManager = new StreamManager();

/** Exported for tests that need a fresh, isolated manager. */
export function createStreamManager() {
    return new StreamManager();
}

/**
 * Wire tab-focus freeze/resume. Returns a detach function.
 * @param {StreamManager} manager
 * @param {() => {key:string,serial:string}[]} getEntries current page provider
 * @param {object} [opts]
 * @param {number} [opts.debounceMs=400] ignore alt-tabs shorter than this
 * @param {string} [opts.base] ws base origin (local transport)
 * @param {'local'|'remote'} [opts.transport] transport selection (remote = Spring)
 * @param {string} [opts.apiBase] Spring API base (remote transport)
 * @param {() => object} [opts.getSyncOpts] read at RESUME time (review M4): the page's CURRENT
 *        resolved route and switches, not the ones captured when this was attached at mount
 * @param {() => boolean} [opts.canResume] false = stay frozen (config not resolved, idle-paused)
 * @param {() => void} [opts.onChange] notified after a freeze/resume happens
 */
export function attachVisibilityAutostop(manager, getEntries, opts = {}) {
    const debounceMs = opts.debounceMs ?? 400;
    let timer = null;
    let frozen = false;

    const onVis = () => {
        if (document.visibilityState !== 'visible') {
            if (timer) clearTimeout(timer);
            timer = setTimeout(() => {
                timer = null;
                manager.stopAll(); // nobody is watching a hidden tab
                frozen = true;
                opts.onChange && opts.onChange('frozen');
            }, debounceMs);
        } else {
            if (timer) {
                clearTimeout(timer);
                timer = null;
            }
            if (frozen) {
                if (opts.canResume && !opts.canResume()) return; // stays frozen until the page resumes it
                frozen = false;
                const syncOpts = opts.getSyncOpts
                    ? opts.getSyncOpts()
                    : { transport: opts.transport, apiBase: opts.apiBase };
                // resume the current page on the CURRENT resolved route
                manager.syncPage(getEntries(), opts.base, syncOpts);
                opts.onChange && opts.onChange('resumed');
            }
        }
    };

    document.addEventListener('visibilitychange', onVis);
    return () => {
        document.removeEventListener('visibilitychange', onVis);
        if (timer) clearTimeout(timer);
    };
}
